import { type AskPunttUserQuestion } from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowRight,
  Check,
  Paperclip,
  X,
  CircleNotch,
} from "@phosphor-icons/react";
import { Badge, Button, Flex, Text, TextField } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";

import { uploadFile } from "../../../services/upload";

export type QuestionAttachment = {
  key: string;
  source: string;
  type: "image" | "video" | "file";
  name?: string;
};

interface QuestionFormProps {
  questions: AskPunttUserQuestion[];
  onSubmit: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

type Selections = Record<number, Set<string>>;
type FreeTexts = Record<number, string>;

type FileItem = {
  id: string;
  file: File;
  status: "pending" | "done" | "error";
  attachment?: QuestionAttachment;
};
type PerQuestionFiles = Record<number, FileItem[]>;

function deriveType(file: File): "image" | "video" | "file" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}

export function QuestionForm({
  questions,
  onSubmit,
  disabled,
  readOnly,
}: QuestionFormProps) {
  const [selections, setSelections] = useState<Selections>(() => {
    const init: Selections = {};
    questions.forEach((_, i) => {
      init[i] = new Set();
    });
    return init;
  });
  const [freeTexts, setFreeTexts] = useState<FreeTexts>(() => {
    const init: FreeTexts = {};
    questions.forEach((_, i) => {
      init[i] = "";
    });
    return init;
  });
  const [submitted, setSubmitted] = useState(readOnly ?? false);
  const [files, setFiles] = useState<PerQuestionFiles>({});
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!readOnly) setSubmitted(false);
  }, [readOnly]);

  const toggleOption = useCallback(
    (qIdx: number, label: string, multiSelect?: boolean) => {
      if (submitted || disabled) return;
      setSelections((prev) => {
        const current = new Set(prev[qIdx]);
        if (multiSelect) {
          if (current.has(label)) current.delete(label);
          else current.add(label);
        } else {
          if (current.has(label)) current.clear();
          else {
            current.clear();
            current.add(label);
          }
        }
        return { ...prev, [qIdx]: current };
      });
    },
    [submitted, disabled],
  );

  const handleFreeText = useCallback(
    (qIdx: number, value: string) => {
      if (submitted || disabled) return;
      setFreeTexts((prev) => ({ ...prev, [qIdx]: value }));
    },
    [submitted, disabled],
  );

  const handleAddFiles = useCallback(
    (qIdx: number, newFiles: FileList | null) => {
      if (!newFiles || newFiles.length === 0 || submitted || disabled) return;

      const items: FileItem[] = Array.from(newFiles).map((f) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file: f,
        status: "pending" as const,
      }));

      setFiles((prev) => ({
        ...prev,
        [qIdx]: [...(prev[qIdx] ?? []), ...items],
      }));

      items.forEach((item) => {
        uploadFile(item.file)
          .then((r) => {
            setFiles((prev) => ({
              ...prev,
              [qIdx]: (prev[qIdx] ?? []).map((p) =>
                p.id === item.id
                  ? {
                      ...p,
                      status: "done" as const,
                      attachment: {
                        key: r.key,
                        source: r.location,
                        type: deriveType(p.file),
                        name: p.file.name,
                      },
                    }
                  : p,
              ),
            }));
          })
          .catch(() => {
            setFiles((prev) => ({
              ...prev,
              [qIdx]: (prev[qIdx] ?? []).map((p) =>
                p.id === item.id ? { ...p, status: "error" as const } : p,
              ),
            }));
          });
      });
    },
    [submitted, disabled],
  );

  const removeFile = useCallback(
    (qIdx: number, fileId: string) => {
      if (submitted || disabled) return;
      setFiles((prev) => ({
        ...prev,
        [qIdx]: (prev[qIdx] ?? []).filter((f) => f.id !== fileId),
      }));
    },
    [submitted, disabled],
  );

  const hasPendingUploads = Object.values(files).some((items) =>
    items.some((f) => f.status === "pending"),
  );

  const hasAnyAnswer = questions.some((_, i) => {
    const hasSelection = (selections[i]?.size ?? 0) > 0;
    const hasText = !!freeTexts[i]?.trim();
    const hasFiles = (files[i] ?? []).some(
      (f) => f.status === "done" && f.attachment,
    );
    return hasSelection || hasText || hasFiles;
  });

  const handleSubmit = useCallback(() => {
    if (!hasAnyAnswer || submitted || disabled || hasPendingUploads) return;

    const lines: string[] = [];
    const allAttachments: QuestionAttachment[] = [];

    questions.forEach((q, i) => {
      const selected = Array.from(selections[i] ?? []);
      const free = freeTexts[i]?.trim();
      const answer = free || selected.join(", ") || "(no answer)";

      const doneFiles = (files[i] ?? [])
        .filter((f) => f.status === "done" && f.attachment)
        .map((f) => f.attachment!);

      doneFiles.forEach((a) => allAttachments.push(a));

      const filesStr =
        doneFiles.length > 0
          ? `\nFiles: ${doneFiles.map((a) => a.name ?? a.source).join(", ")}`
          : "";

      lines.push(`**${q.header}:** ${answer}${filesStr}`);
    });

    setSubmitted(true);
    onSubmit(
      lines.join("\n"),
      allAttachments.length > 0 ? allAttachments : undefined,
    );
  }, [
    hasAnyAnswer,
    submitted,
    disabled,
    hasPendingUploads,
    questions,
    selections,
    freeTexts,
    files,
    onSubmit,
  ]);

  return (
    <div className="mt-3 animate-fade-in space-y-3">
      {questions.map((q, qIdx) => {
        const selected = selections[qIdx] ?? new Set();
        const freeText = freeTexts[qIdx] ?? "";
        const questionFiles = files[qIdx] ?? [];
        const showFileUpload = q.allowFiles && !submitted;

        return (
          <div
            key={qIdx}
            className="rounded-xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-1 p-4 shadow-sm"
          >
            <Flex align="center" gap="2" className="mb-2">
              <Badge size="1" variant="soft" color="blue">
                {q.header}
              </Badge>
            </Flex>

            <Text
              as="p"
              size="2"
              weight="medium"
              className="mb-3 text-puntt-neutral-gray-12"
            >
              {q.question}
            </Text>

            <Flex wrap="wrap" gap="2" className="mb-2">
              {q.options.map((opt) => {
                const isSelected = selected.has(opt.label);
                return (
                  <button
                    key={opt.label}
                    type="button"
                    disabled={submitted || disabled}
                    onClick={() => toggleOption(qIdx, opt.label, q.multiSelect)}
                    className={`group flex items-center gap-1.5 rounded-lg border px-3 py-2 text-left transition-all duration-200 ${
                      isSelected
                        ? "border-puntt-accent-9 bg-puntt-accent-3 shadow-sm"
                        : "border-puntt-neutral-gray-5 bg-base-white hover:border-puntt-accent-7 hover:bg-puntt-accent-2/30"
                    } ${submitted || disabled ? "cursor-default opacity-75" : "cursor-pointer"}`}
                    title={opt.description}
                  >
                    {isSelected && (
                      <Check
                        size={14}
                        weight="bold"
                        className="shrink-0 text-puntt-accent-9"
                      />
                    )}
                    <div className="min-w-0">
                      <Text
                        size="2"
                        weight={isSelected ? "medium" : "regular"}
                        className={
                          isSelected
                            ? "text-puntt-accent-11"
                            : "text-puntt-neutral-gray-12"
                        }
                      >
                        {opt.label}
                      </Text>
                      {opt.description && (
                        <Text
                          as="p"
                          size="1"
                          className="mt-0.5 text-puntt-neutral-gray-9"
                        >
                          {opt.description}
                        </Text>
                      )}
                    </div>
                  </button>
                );
              })}
            </Flex>

            {submitted && freeText.trim() ? (
              <Flex align="center" gap="2" className="mt-2">
                <div className="flex-1 rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 px-3 py-2">
                  <Text size="2" className="text-puntt-neutral-gray-12">
                    {freeText.trim()}
                  </Text>
                </div>
              </Flex>
            ) : !submitted ? (
              <Flex align="center" gap="2" className="mt-2">
                <div className="flex-1">
                  <TextField.Root
                    size="2"
                    variant="surface"
                    placeholder="Or type your own answer..."
                    value={freeText}
                    onChange={(e) => handleFreeText(qIdx, e.target.value)}
                  />
                </div>
                {showFileUpload && (
                  <>
                    <input
                      ref={(el) => {
                        fileInputRefs.current[qIdx] = el;
                      }}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        handleAddFiles(qIdx, e.target.files);
                        e.target.value = "";
                      }}
                    />
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => fileInputRefs.current[qIdx]?.click()}
                      className="flex shrink-0 items-center justify-center rounded-lg border border-puntt-neutral-gray-5 bg-base-white p-2 text-puntt-neutral-gray-9 transition-colors hover:border-puntt-accent-7 hover:text-puntt-accent-9"
                      title="Attach files"
                    >
                      <Paperclip size={16} />
                    </button>
                  </>
                )}
              </Flex>
            ) : null}

            {questionFiles.length > 0 && (
              <Flex gap="2" wrap="wrap" className="mt-2">
                {questionFiles.map((item) => (
                  <Flex
                    key={item.id}
                    align="center"
                    gap="1"
                    className="rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-2 py-1"
                  >
                    {item.status === "pending" && (
                      <CircleNotch
                        size={12}
                        className="animate-spin text-puntt-accent-9"
                      />
                    )}
                    <Text
                      size="1"
                      className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                    >
                      {item.file.name}
                      {item.status === "error" && " (failed)"}
                    </Text>
                    {!submitted && (
                      <button
                        type="button"
                        onClick={() => removeFile(qIdx, item.id)}
                        className="ml-0.5 text-puntt-neutral-gray-8 hover:text-puntt-neutral-gray-12"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </Flex>
                ))}
              </Flex>
            )}
          </div>
        );
      })}

      {!submitted && (
        <Flex justify="end">
          <Button
            size="2"
            variant="solid"
            color="blue"
            disabled={!hasAnyAnswer || disabled || hasPendingUploads}
            onClick={handleSubmit}
            className="cursor-pointer"
          >
            {hasPendingUploads ? "Uploading..." : "Submit Answers"}
            <ArrowRight size={14} weight="bold" />
          </Button>
        </Flex>
      )}
    </div>
  );
}
